import { FieldValues, Path, useFormContext } from "react-hook-form";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./form";
import { Input } from "alurkerja-ui";
import { RequiredAsterisk } from "./required-asterisk";
import { cn } from "@/utils/helper";

interface FieldInputProps<TFormValues extends FieldValues> {
  name: Path<TFormValues>;
  label?: string;
  hideLabel?: boolean;
  placeholder?: string;
  description?: string;
  type?: "text" | "number" | "file";
  accept?: string;
  isDisabled?: boolean;
  isVisuallyHidden?: boolean;
  hasRequiredAsterisk?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  title?: string;
  min?: number;
  transform?: (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => React.ChangeEvent<HTMLInputElement>;
  uppercase?: boolean;
  unstyle?: boolean;
}
export function FieldInput<TFormValues extends FieldValues>({
  name,
  label,
  hideLabel,
  placeholder,
  description,
  type = "text",
  accept,
  isDisabled = false,
  isVisuallyHidden = false,
  hasRequiredAsterisk = false,
  onChange,
  onKeyDown,
  title,
  min,
  transform,
  uppercase = false,
  unstyle = false,
}: FieldInputProps<TFormValues>) {
  const handleChange = onChange;
  const form = useFormContext();

  const handleUppercaseTransform = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (uppercase && type === "text") {
      const newEvent = { ...e };
      newEvent.target = {
        ...e.target,
        value: e.target.value.toUpperCase(),
      };
      return newEvent as React.ChangeEvent<HTMLInputElement>;
    }

    return e;
  };

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field: { onChange, ...fieldProps } }) => (
        <FormItem
          className={cn(
            `${isVisuallyHidden ? "sr-only" : "break-inside-avoid-column"}${
              hideLabel ? " space-y-0" : ""
            }`,
            unstyle && "flex w-full flex-col items-start gap-1 !space-y-0",
          )}
        >
          {label && (
            <FormLabel
              className={cn(
                hideLabel ? "sr-only" : "",
                unstyle && "text-gray-500",
              )}
            >
              {label}
            </FormLabel>
          )}
          {hasRequiredAsterisk && <RequiredAsterisk />}
          <FormControl>
            {type === "file" ? (
              <Input
                name={fieldProps.name}
                type="file"
                accept={accept}
                disabled={isDisabled}
                onChange={(e) => {
                  onChange(e.target.files?.[0]);
                  handleChange?.(e);
                }}
                className="dark:bg-neutral-50"
                title={title}
              />
            ) : (
              <Input
                {...fieldProps}
                onChange={(e) => {
                  const processedEvent = handleUppercaseTransform(e);
                  const finalEvent = transform
                    ? transform(processedEvent)
                    : processedEvent;
                  onChange(finalEvent);

                  handleChange?.(finalEvent);
                }}
                type={type}
                disabled={isDisabled}
                placeholder={placeholder}
                title={title}
                onKeyDown={onKeyDown}
                min={min}
                className={cn("text-black")}
                unstyle={unstyle}
              />
            )}
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
